/* ============================================================
   colorModeView — ecfe2257 (Artem 2026-08-28 §5): pure view
   helpers for the customer color modes and hex swatches.

   Modes: concrete performer stock color; «Многоцветная» (AMS —
   no up-front slot mapping, details clarified in order
   comments/chat); «Любой цвет» (bypasses per-color stock
   limits, total material availability only — matches backend
   MaterialStockRules.LimitsByTotalOnly).

   Swatches: provider stock rows carry an optional #RRGGBB
   value — color chips render it as a circular swatch, neutral
   fallback when absent. Pure functions; no API calls.
   ============================================================ */

import type { CSSProperties } from 'react';
import type { ColorSwatch } from '../types/product';

/** Mirrors backend PrintColorMode (0 any / 1 multicolor / 2 concrete). */
export type PrintColorMode = 'any' | 'multicolor' | 'concrete';

export const PRINT_COLOR_MODES: Record<PrintColorMode, number> = {
  any: 0,
  multicolor: 1,
  concrete: 2,
};

/** Map the raw API number to the view mode (unknown → any, legacy-safe). */
export function printColorModeFromApi(value: number | null | undefined): PrintColorMode {
  if (value === 1) return 'multicolor';
  if (value === 2) return 'concrete';
  return 'any';
}

/** Chip captions for the two non-concrete modes (concrete = the color name). */
export const ANY_COLOR_CHIP = 'Любой цвет';
export const MULTICOLOR_CHIP = 'Многоцветная';

/** Note under the picker once a mode chip is active. */
export function colorModeNote(mode: PrintColorMode): string | null {
  switch (mode) {
    case 'multicolor':
      return 'Многоцветная (AMS): слоты цветов уточним с исполнителем в комментариях к заказу и переписке.';
    case 'any':
      return 'Любой цвет: подойдёт любой доступный цвет материала исполнителя.';
    default:
      return null;
  }
}

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

/** #RRGGBB check (mirror of the backend rule). */
export function isValidHex(hex: string | null | undefined): boolean {
  return typeof hex === 'string' && HEX_RE.test(hex.trim());
}

/**
 * Build a color→hex lookup from the offered swatches. First swatch wins
 * (backend order = most remaining first); invalid/absent hex stays out.
 */
export function hexByColor(swatches: readonly ColorSwatch[] | null | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const s of swatches ?? []) {
    if (typeof s?.color !== 'string') continue;
    const key = s.color.toLowerCase();
    if (!map.has(key) && isValidHex(s.hexColor)) map.set(key, s.hexColor!.trim().toUpperCase());
  }
  return map;
}

/** Inline style for the circular swatch dot; neutral when no valid hex. */
export function swatchDotStyle(hex: string | null | undefined): CSSProperties {
  const valid = isValidHex(hex);
  return {
    display: 'inline-block',
    width: 14,
    height: 14,
    borderRadius: '50%',
    marginRight: 6,
    verticalAlign: '-2px',
    background: valid ? hex!.trim() : 'var(--color-separator, #d9d9de)',
    border: '1px solid rgba(0,0,0,0.12)',
    flexShrink: 0,
  };
}

/**
 * ac930ee2: name→hex fallback for free-text color names — client mirror of
 * the backend MaterialColorHex for the standard palette plus the most
 * common hand-typed names. Covers surfaces without stock swatches (the
 * standard-palette picker posture, provider cabinet rows whose hex is not
 * set yet); RAL and the long tail resolve server-side into the swatch DTOs.
 */
const NAME_HEX_FALLBACK: Readonly<Record<string, string>> = {
  'Чёрный': '#3A3A3C',
  'Белый': '#F2F2F7',
  'Серый': '#8E8E93',
  'Красный': '#D94848',
  'Синий': '#4A6FD4',
  'Зелёный': '#4CAF6D',
  'Жёлтый': '#E8B931',
  'Оранжевый': '#F08C1B',
  'Прозрачный': '#BFD4E4',
  'Натуральный': '#D6CBB5',
  'Голубой': '#7EB6E8',
  'Бирюзовый': '#4FC3C7',
  'Розовый': '#F2A0B5',
  'Фиолетовый': '#8E6FC8',
  'Коричневый': '#7A4E2D',
  'Бордовый': '#7B2D3E',
  'Золотой': '#D4AF37',
  'Серебристый': '#C0C4CC',
};

/** Hex for a free-text color name (exact or ё/е-insensitive); null when unknown. */
export function fallbackHexFor(colorName: string | null | undefined): string | null {
  if (colorName == null) return null;
  const direct = NAME_HEX_FALLBACK[colorName];
  if (direct != null) return direct;
  const normalized = colorName.trim().toLowerCase().replace(/ё/g, 'е');
  for (const [name, hex] of Object.entries(NAME_HEX_FALLBACK)) {
    if (name.toLowerCase().replace(/ё/g, 'е') === normalized) return hex;
  }
  return null;
}
